import { useCallback, useEffect } from "react";

import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useLoaderData } from "react-router";
import { toast } from "sonner";

import { onToast } from "@components/Toast";

import { useChatStore } from "@storage/global/useChatStore";
import { useToysStore } from "@storage/global/useToysStore";
import { useWebSocketStore } from "@storage/global/useWebSocketStore";
import { TSessionData } from "@storage/session/types";
import { currentRoomChatIdStorage } from "@storage/sessionStorage";

import { QUERY_KEYS } from "@configs/api";
import { DELAY_TIME_1000 } from "@configs/constants";
import { ENV_CONFIG } from "@configs/env";

import { reconnectWs } from "./helpers";
import { webSocketHandleEvents } from "./webSocketHandleEvents";

const AMOUNT_ATTEMPTS_RECONNECT = 30;

let reconnectWsAttempt = 0;
let ws: WebSocket | null = null;
let isLoggingOut = false;
let shouldInvalidateCallQueriesAfterOnlineReconnect = false;
const NOTIFICATION_ID = "no-connection-server";

/** Close WebSocket and disable auto-reconnect (e.g. on logout). */
export const closeWebSocket = (disableReconnect = false) => {
	if (disableReconnect) {
		isLoggingOut = true;
	}
	if (ws) {
		ws.onmessage = () => {};
		ws.close();
		ws = null;
	}
};

/** Max waiting time for reconnect around 12.525 minutes */
const getReconnectDelay = (attempt: number, firstDelayMs: number = 500, maxDelayMs: number = 30_000): number => {
	if (attempt === 0) return firstDelayMs;

	return Math.min(firstDelayMs * Math.pow(2, attempt), maxDelayMs);
};

const showErrorNotify = () => {
	onToast({
		id: NOTIFICATION_ID,
		message: "Error",
		description: "There is no connection to the server.",
	});
};

const invalidateCallQueries = (queryClient: QueryClient) => {
	queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
	queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRIVATE_REQUEST_DATA] });
	queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_GOAL_LIST] });
	queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.VERIFICATION_INFO] });
};

export const WebSocketManager = () => {
	const queryClient = useQueryClient();

	const { currentAccount } = useLoaderData<TSessionData>();
	const store = useWebSocketStore.getState();
	const chatStore = useChatStore.getState();
	const toysStore = useToysStore.getState();

	const combineStores = { ...store, ...chatStore, ...toysStore };
	const currentRoomChatId = currentRoomChatIdStorage.getItem();

	const connectToWebsocket = useCallback(() => {
		isLoggingOut = false; // Allow reconnects when (re)connecting (e.g. after login)

		if (!ws) {
			ws = new WebSocket(ENV_CONFIG.BASE_SOCKET_URL || "");
			const HANDLERS = webSocketHandleEvents(combineStores, queryClient, currentAccount, ws);

			ws.onopen = () => {
				setTimeout(function () {
					if (ws === null) return;

					ws.send(JSON.stringify(reconnectWs(currentRoomChatId || currentAccount.roomChatId)));

					if (shouldInvalidateCallQueriesAfterOnlineReconnect) {
						invalidateCallQueries(queryClient);
						toysStore.triggerToysSession(toysStore.activePrivateShowId ?? "");
						shouldInvalidateCallQueriesAfterOnlineReconnect = false;
					}

					toast.dismiss(NOTIFICATION_ID);
					reconnectWsAttempt = 0;
				}, DELAY_TIME_1000);
			};

			ws.onmessage = (event) => {
				try {
					const message = JSON.parse(event.data);

					const handler =
						HANDLERS[message.type as keyof ReturnType<typeof webSocketHandleEvents>] || HANDLERS["DEFAULT"];
					handler(message);
				} catch (e) {
					console.error("Error processing WS message:", e);
				}
			};

			ws.onclose = () => {
				ws = null;
				if (!isLoggingOut) {
					setTimeout(function () {
						connectToWebsocket();
					}, getReconnectDelay(reconnectWsAttempt));
					reconnectWsAttempt += 1;
				}
			};

			ws.onerror = () => {
				if (ws) {
					if (reconnectWsAttempt >= AMOUNT_ATTEMPTS_RECONNECT) return;
					showErrorNotify();
					ws.close();
				}
			};
		}

		window.addEventListener("offline", () => {
			showErrorNotify();
		});

		window.addEventListener("online", () => {
			chatStore.clearMessageList();
			toast.dismiss(NOTIFICATION_ID);
			shouldInvalidateCallQueriesAfterOnlineReconnect = true;
		});

		return () => {
			if (ws !== null) {
				ws.close();
			}
		};
	}, []);

	useEffect(() => {
		connectToWebsocket();
	}, [connectToWebsocket]);

	return null;
};
